import { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import axios, { clearAxiosCache } from "../../axios-config";
import { Link, useParams } from 'react-router-dom';
import { Package, Edit, Trash2, Plus, LogOut, Settings, ArrowLeft } from 'lucide-react';
import { badgeService } from '../../services/badgeService';
import MediaChooser from '../common/MediaChooser';

// Define the Product interface based on API response
interface Product {
    productId: number;
    storeId: number;
    productName: string;
    productDescription: string;
    imageUrl: string;
    pointCost: number;
    salePointCost: number;
    stockQuantity: number;
    productUnlimited: boolean;
    maxPerStudent: number;
    featuredStart: string;
    featuredEnd: string;
    pickupLocation: string;
    createdAt: string;
    createdBy: number;
    updatedAt: string;
    updatedBy: number;
    requiredBadgeId?: string;
    requiredBadgeLevel?: number;
    requiredBadgeLevelId?: number | null;
}

const ProductEditor = () => {
    //const [storeId] = useState<number>(1); // set current StoreID as needed
    const { storeId } = useParams<{ storeId: string }>();
    const [products, setProducts] = useState<Product[]>([]);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [isFormVisible, setIsFormVisible] = useState<boolean>(false);
    const [badgeOptions, setBadgeOptions] = useState<{ badgeLevelId: number; badgeId: string; levelNumber: number; label: string; }[]>([]);

    // Fetch product list
    useEffect(() => {
        if (!storeId) return;
        fetchProducts();
        (async () => {
            try {
                const badges = await badgeService.getBadges();
                const options = badges.flatMap(badge =>
                    badge.badgeLevels.map(level => ({
                        badgeLevelId: level.badgeLevelId,
                        badgeId: badge.badgeId,
                        levelNumber: level.levelNumber,
                        label: `${badge.badgeName} - ${level.levelName}`
                    }))
                );
                setBadgeOptions(options);
            } catch (err) {
                console.error(err);
            }
        })();
    }, [storeId]);

    const fetchProducts = async () => {
        try {
            const res = await axios.get(`/api/Products/${storeId}`);
            setProducts(res.data);
        } catch (error) {
            console.error(error);
        }
    };

    // Handle form field changes
    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!editingProduct) return;
        setEditingProduct({
            ...editingProduct,
            [e.target.name]: e.target.value,
        });
    };

    // Toggle boolean field
    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!editingProduct) return;
        setEditingProduct({
            ...editingProduct,
            [e.target.name]: e.target.checked,
        });
    };

    const handleBadgeLevelSelect = (e: ChangeEvent<HTMLSelectElement>) => {
        if (!editingProduct) return;
        const value = e.target.value;
        const levelId = value ? Number(value) : null;
        let badgeId = '';
        let levelNumber = 0;
        if (levelId !== null) {
            const opt = badgeOptions.find(o => o.badgeLevelId === levelId);
            if (opt) {
                badgeId = opt.badgeId;
                levelNumber = opt.levelNumber;
            }
        }
        setEditingProduct({
            ...editingProduct,
            requiredBadgeId: badgeId,
            requiredBadgeLevel: levelNumber,
            requiredBadgeLevelId: levelId,
        });
    };

    // Open form to create new product
    const handleNew = () => {
        setEditingProduct({
            productId: 0,
            storeId: Number(storeId),
            productName: '',
            productDescription: '',
            imageUrl: '',
            pointCost: 0,
            salePointCost: 0,
            stockQuantity: 0,
            productUnlimited: false,
            maxPerStudent: 0,
            featuredStart: '',
            featuredEnd: '',
            pickupLocation: '',
            createdAt: '',
            createdBy: 0,
            updatedAt: '',
            updatedBy: 0,
            requiredBadgeId: '',
            requiredBadgeLevel: 0,
            requiredBadgeLevelId: null,
        });
        setIsFormVisible(true);
    };

    // Open form to edit existing product
    const handleEdit = (product: Product) => {
        setEditingProduct(product);
        setIsFormVisible(true);
    };

    // Delete product
    const handleDelete = async (id: number) => {
        try {
            await axios.delete(`/api/Products/${id}`);
            fetchProducts();
        } catch (error) {
            console.error(error);
        }
    };

    // Handle form submission for create/update
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!editingProduct) return;

        // Build payload with required fields
        const payload: any = {
            productName: editingProduct.productName,
            storeId: Number(storeId),
            pointCost: Number(editingProduct.pointCost),
        };

        // Optionally add fields only if they differ from their defaults
        if (editingProduct.productDescription && editingProduct.productDescription !== '') {
            payload.productDescription = editingProduct.productDescription;
        }
        if (editingProduct.imageUrl && editingProduct.imageUrl !== '') {
            payload.imageUrl = editingProduct.imageUrl;
        }
        if (editingProduct.salePointCost && Number(editingProduct.salePointCost) !== 0) {
            payload.salePointCost = Number(editingProduct.salePointCost);
        }
        if (editingProduct.stockQuantity && Number(editingProduct.stockQuantity) !== 0) {
            payload.stockQuantity = Number(editingProduct.stockQuantity);
        }
        if (editingProduct.productUnlimited === true) {
            payload.productUnlimited = true;
        }
        if (editingProduct.maxPerStudent && Number(editingProduct.maxPerStudent) !== 0) {
            payload.maxPerStudent = Number(editingProduct.maxPerStudent);
        }
        if (editingProduct.featuredStart) {
            payload.featuredStart = new Date(editingProduct.featuredStart).toISOString();
        }
        if (editingProduct.featuredEnd) {
            payload.featuredEnd = new Date(editingProduct.featuredEnd).toISOString();
        }
        if (editingProduct.pickupLocation && editingProduct.pickupLocation !== '') {
            payload.pickupLocation = editingProduct.pickupLocation;
        }
        if (editingProduct.requiredBadgeLevelId) {
            payload.requiredBadgeLevelId = editingProduct.requiredBadgeLevelId;
        }

        try {
            if (editingProduct.productId === 0) {
                await axios.post(`/api/Products`, payload);
            } else {
                payload.productId = editingProduct.productId;
                await axios.put(`/api/Products/${editingProduct.productId}`, payload);
            }
            setIsFormVisible(false);
            clearAxiosCache();
            fetchProducts();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className="space-y-6 bg-gradient-to-br from-indigo-50 to-blue-100 p-4 rounded-xl">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/stores"
                className="p-2 hover:bg-gray-100/80 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <h1 className="text-2xl font-bold text-gray-800">Products</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <button
                  className="p-2 hover:bg-gray-100/80 rounded-full transition-colors"
                  title="Settings"
                >
                  <Settings className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  className="p-2 hover:bg-gray-100/80 rounded-full transition-colors"
                  title="Log out"
                >
                  <LogOut className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
            {/* Product List Section */}
            {!isFormVisible && (
                <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <Package className="w-6 h-6 text-indigo-500" />
                            <h2 className="text-xl font-semibold text-gray-800">Products</h2>
                        </div>
                        <button 
                            onClick={handleNew} 
                            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            New Product
                        </button>
                    </div>
                    <div className="overflow-hidden bg-white/70 rounded-lg">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="p-2 text-left text-gray-700 font-medium">Name</th>
                                    <th className="p-2 text-left text-gray-700 font-medium">Description</th>
                                    <th className="p-2 text-left text-gray-700 font-medium">Points</th>
                                    <th className="p-2 text-left text-gray-700 font-medium">Pickup Location</th>
                                    <th className="p-2 text-left text-gray-700 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map(prod => (
                                    <tr key={prod.productId} className="border-b border-gray-100 hover:bg-indigo-50/50 transition-colors">
                                        <td className="p-3 text-gray-800">{prod.productName}</td>
                                        <td className="p-3 text-gray-800">{prod.productDescription}</td>
                                        <td className="p-3 text-gray-800">{prod.pointCost}</td>
                                        <td className="p-3 text-gray-800">{prod.pickupLocation || 'N/A'}</td>
                                        <td className="p-3 space-x-2">
                                            <button 
                                                onClick={() => handleEdit(prod)} 
                                                className="px-3 py-1 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors inline-flex items-center gap-1"
                                            >
                                                <Edit className="w-4 h-4" />
                                                Edit
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(prod.productId)} 
                                                className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors inline-flex items-center gap-1"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Product Form Section */}
            {isFormVisible && editingProduct && (
                <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">{editingProduct.productId === 0 ? 'New Product' : 'Edit Product'}</h2>
                    <form onSubmit={handleSubmit}>
                        {/* Form Field */}
                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Name:</label>
                            <input 
                                title="Product Name"
                                type="text" 
                                name="productName" 
                                value={editingProduct.productName} 
                                onChange={handleChange} 
                                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                            />
                        </div>
                        {/* Form Field */}
                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Description:</label>
                            <textarea 
                                title="Description"
                                name="productDescription" 
                                value={editingProduct.productDescription} 
                                onChange={handleChange} 
                                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                            />
                        </div>
                        {/* Image chooser */}
                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Image:</label>
                            <MediaChooser
                              label="Product Image"
                              value={editingProduct.imageUrl}
                              onChange={(v) => setEditingProduct(prev => (prev ? { ...prev, imageUrl: v } : prev))}
                              allowManualUrlEntry={true}
                              buttonText="Choose Image"
                              clearable={true}
                              imagesOnly={true}
                            />
                        </div>
                        {/* Form Field */}
                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Point Cost:</label>
                            <input
                                title="Point Cost"
                                type="number" 
                                name="pointCost" 
                                value={editingProduct.pointCost} 
                                onChange={handleChange} 
                                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                            />
                        </div>
                        {/* Form Field */}
                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Sale Point Cost:</label>
                            <input 
                                title="Sale Point Cost"
                                type="number" 
                                name="salePointCost" 
                                value={editingProduct.salePointCost} 
                                onChange={handleChange} 
                                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                            />
                        </div>
                        {/* Form Field */}
                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Stock Quantity:</label>
                            <input 
                                title="Stock Quantity"
                                type="number" 
                                name="stockQuantity" 
                                value={editingProduct.stockQuantity} 
                                onChange={handleChange} 
                                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                            />
                        </div>
                        {/* Checkbox Field */}
                        <div className="mb-4 flex items-center space-x-2">
                            <label className="font-medium">Unlimited:</label>
                            <input 
                                title="Unlimited"
                                type="checkbox" 
                                name="productUnlimited" 
                                checked={editingProduct.productUnlimited} 
                                onChange={handleCheckboxChange} 
                                className="form-checkbox h-5 w-5 text-indigo-600"
                            />
                        </div>
                        {/* Form Field */}
                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Max Per Student:</label>
                            <input 
                                title="Max Per Student"
                                type="number" 
                                name="maxPerStudent" 
                                value={editingProduct.maxPerStudent} 
                                onChange={handleChange} 
                                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                            />
                        </div>
                        {/* Form Field */}
                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Pickup Location:</label>
                            <input
                                title="Pickup Location"
                                type="text"
                                name="pickupLocation"
                                value={editingProduct.pickupLocation}
                                onChange={handleChange}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                                placeholder="e.g., Room 101, Main Office"
                            />
                        </div>
                        {/* Badge Requirement */}
                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Required Badge Level:</label>
                            <select
                                name="requiredBadgeLevelId"
                                value={editingProduct.requiredBadgeLevelId ?? ''}
                                onChange={handleBadgeLevelSelect}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                            >
                                <option value="">None</option>
                                {badgeOptions.map(opt => (
                                    <option key={opt.badgeLevelId} value={opt.badgeLevelId}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        {/* Form Field */}
                        <div className="mb-4 grid grid-cols-2 gap-4">
                            <div>
                                <label className="block mb-1 font-medium">Featured Start:</label>
                                <input 
                                    title='Featured Start'
                                    type="datetime-local" 
                                    name="featuredStart" 
                                    value={editingProduct.featuredStart ? editingProduct.featuredStart.substring(0,16) : ''} 
                                    onChange={handleChange} 
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block mb-1 font-medium">Featured End:</label>
                                <input 
                                    title='Featured End'
                                    type="datetime-local" 
                                    name="featuredEnd" 
                                    value={editingProduct.featuredEnd ? editingProduct.featuredEnd.substring(0,16) : ''} 
                                    onChange={handleChange} 
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                                />
                            </div>
                        </div>
                        <div className="flex space-x-3">
                            <button type="submit" className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600">Save</button>
                            <button type="button" onClick={() => setIsFormVisible(false)} className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600">Cancel</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ProductEditor;